import { useState } from 'react';
import { intakeAPI } from '../utils/api';
import { format } from 'date-fns';

export default function StatusCheck() {
  const [searchQuery, setSearchQuery] = useState('');
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setError(null);
    setLoading(true);

    try {
      const response = await intakeAPI.getStatus(searchQuery.trim());
      setSubmission(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Submission not found. Please check your ticket number or submission ID.');
      } else {
        setError('An error occurred while checking status. Please try again.');
      }
      setSubmission(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDetails = (status) => {
    const statusMap = {
      pending_review: {
        label: 'Pending Review',
        description: 'Your submission has been received and is waiting to be reviewed by our team.',
        color: 'bg-yellow-100 text-yellow-800',
        icon: '⏳'
      },
      in_review: {
        label: 'In Review',
        description: 'Our team is currently reviewing your submission.',
        color: 'bg-blue-100 text-blue-800',
        icon: '🔍'
      },
      ready_to_submit: {
        label: 'Ready to Submit',
        description: 'Your submission has been reviewed and is ready to be sent for carrier verification.',
        color: 'bg-primary-100 text-primary-800',
        icon: '✅'
      },
      awaiting_profile_approval: {
        label: 'Preparing Submission',
        description: 'We are preparing your verification for carrier review. This step usually completes within a few hours.',
        color: 'bg-primary-100 text-primary-800',
        icon: '⚙️'
      },
      submitted_to_twilio: {
        label: 'Submitted for Review',
        description: 'Your verification request has been submitted for carrier review.',
        color: 'bg-primary-100 text-primary-800',
        icon: '📤'
      },
      awaiting_twilio: {
        label: 'Awaiting Carrier Review',
        description: 'The carrier is reviewing your verification request. This typically takes 1-3 business days.',
        color: 'bg-primary-100 text-primary-800',
        icon: '⏱️'
      },
      approved: {
        label: 'Approved',
        description: 'Your phone number has been verified and approved! You can now use it for messaging.',
        color: 'bg-green-100 text-green-800',
        icon: '🎉'
      },
      rejected: {
        label: 'Needs Attention',
        description: 'Your submission requires additional information. Please check your email for details on what to update.',
        color: 'bg-red-100 text-red-800',
        icon: '⚠️'
      },
      resubmitted: {
        label: 'Resubmitted',
        description: 'Your updated submission has been resubmitted for carrier review.',
        color: 'bg-primary-100 text-primary-800',
        icon: '🔄'
      },
      archived: {
        label: 'Archived',
        description: 'This request has been archived. If you believe this is an error, please contact our team.',
        color: 'bg-gray-100 text-gray-800',
        icon: '🗄️'
      }
    };

    return statusMap[status] || {
      label: 'In Progress',
      description: 'Your request is in progress and our team is working on it.',
      color: 'bg-primary-100 text-primary-800',
      icon: 'ℹ️'
    };
  };

  const getEstimatedTimeline = (status, submittedAt) => {
    const timelines = {
      pending_review: '1-2 business days',
      in_review: '1-2 business days',
      ready_to_submit: 'Within 24 hours',
      awaiting_profile_approval: 'Usually within a few hours',
      submitted_to_twilio: '1-3 business days',
      awaiting_twilio: '1-3 business days',
      approved: 'Complete',
      rejected: 'Waiting for additional information',
      resubmitted: '1-3 business days',
      archived: '—'
    };

    return timelines[status] || 'In progress';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Check Verification Status</h1>
          <p className="mt-2 text-gray-600">
            Enter your ticket number or submission ID to check the status of your text messaging verification request.
          </p>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Form */}
        <div className="card mb-6">
          <form onSubmit={handleSearch}>
            <label htmlFor="search" className="label">
              Ticket Number or Submission ID
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g., 12345 or abc-123-def-456"
                className="input flex-1"
                disabled={loading}
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || !searchQuery.trim()}
              >
                {loading ? 'Searching...' : 'Check Status'}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Results */}
        {submission && (
          <div className="space-y-6">
            {/* Status Card */}
            <div className="card">
              <div className="flex items-start gap-4">
                <div className="text-4xl">{getStatusDetails(submission.status).icon}</div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {submission.organization_name}
                    </h2>
                    <span className={`badge ${getStatusDetails(submission.status).color} text-sm px-3 py-1`}>
                      {getStatusDetails(submission.status).label}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">
                    {getStatusDetails(submission.status).description}
                  </p>

                  {submission.status === 'rejected' && submission.rejection_reason && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-sm font-medium text-red-900 mb-1">Additional Information Needed:</p>
                      <p className="text-sm text-red-800">{submission.rejection_reason}</p>
                    </div>
                  )}

                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-gray-500">Phone Number</dt>
                      <dd className="text-gray-900 font-medium">{submission.phone_number}</dd>
                    </div>
                    {submission.ticket_id && (
                      <div>
                        <dt className="text-gray-500">Ticket Number</dt>
                        <dd className="text-gray-900 font-medium">#{submission.ticket_id}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Submitted</dt>
                      <dd className="text-gray-900">
                        {format(new Date(submission.submitted_at), 'MMM d, yyyy h:mm a')}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Estimated Timeline</dt>
                      <dd className="text-gray-900">
                        {getEstimatedTimeline(submission.status, submission.submitted_at)}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {/* Progress Timeline */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress Timeline</h3>
              <div className="space-y-4">
                {[
                  { status: 'pending_review', label: 'Received' },
                  { status: 'in_review', label: 'Under Review' },
                  { status: 'ready_to_submit', label: 'Ready for Submission' },
                  { status: 'submitted_to_twilio', label: 'Submitted for Review' },
                  { status: 'awaiting_twilio', label: 'Awaiting Carrier Review' },
                  { status: 'approved', label: 'Approved' }
                ].map((step, idx, arr) => {
                  const stepStatuses = ['pending_review', 'in_review', 'ready_to_submit', 'submitted_to_twilio', 'awaiting_twilio', 'approved'];
                  // Map non-linear statuses to the furthest step they have reached
                  // so that rejected/resubmitted/in-prep requests still show progress.
                  const statusToStepIndex = {
                    pending_review: 0,
                    in_review: 1,
                    ready_to_submit: 2,
                    awaiting_profile_approval: 2,
                    submitted_to_twilio: 3,
                    resubmitted: 3,
                    awaiting_twilio: 4,
                    rejected: 4,
                    approved: 5
                  };
                  const currentIndex = statusToStepIndex[submission.status] ?? -1;
                  const stepIndex = stepStatuses.indexOf(step.status);
                  const needsAttention = submission.status === 'rejected';
                  const isComplete = stepIndex <= currentIndex;
                  const isCurrent = stepIndex === currentIndex;

                  return (
                    <div key={step.status} className="flex items-start">
                      <div className="flex flex-col items-center mr-4">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            needsAttention && isCurrent
                              ? 'bg-red-500 text-white'
                              : isComplete
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 text-gray-500'
                          } ${isCurrent ? (needsAttention ? 'ring-4 ring-red-200' : 'ring-4 ring-primary-200') : ''}`}
                        >
                          {needsAttention && isCurrent ? (
                            <span className="text-sm font-bold">!</span>
                          ) : isComplete ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            <span className="text-sm">{idx + 1}</span>
                          )}
                        </div>
                        {idx < arr.length - 1 && (
                          <div
                            className={`w-0.5 h-12 ${
                              isComplete ? 'bg-primary-600' : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 pb-8">
                        <p
                          className={`text-sm font-medium ${
                            isComplete ? 'text-gray-900' : 'text-gray-500'
                          }`}
                        >
                          {step.label}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Help Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-900 mb-1">Need Help?</p>
                  <p className="text-sm text-blue-800">
                    If you have questions about your submission, please reply to the email confirmation you received or contact our support team.
                    {submission.ticket_id && (
                      <> Reference ticket number <strong>#{submission.ticket_id}</strong> in your message.</>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
